import React, { useState } from "react";
import { signIn } from "aws-amplify/auth";
import { useNavigate, useLocation } from "react-router-dom";

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/main";

  const [form, setForm] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      await signIn({
        username: form.email.toLowerCase(),
        password: form.password,
      });

      // After successful sign-in, navigate to main app
      navigate(from, { replace: true });
    } catch (error) {
      console.error("Sign-in error:", error);
      console.error("Error name:", error.name);
      console.error("Error message:", error.message);

      // Handle different error types
      if (error.name === "UserNotConfirmedException") {
        setErr("Please verify your email first. Check your inbox for the verification code.");
        setTimeout(() => {
          navigate(`/verify?email=${encodeURIComponent(form.email)}`);
        }, 2000);
      } else if (error.name === "NotAuthorizedException") {
        // Check if it's specifically because user is disabled
        if (error.message?.toLowerCase().includes("disabled") ||
            error.message?.toLowerCase().includes("not enabled")) {
          setErr("Your account is still pending admin approval. Please wait for a confirmation email.");
        } else {
          setErr("Incorrect email or password. Please try again.");
        }
      } else if (error.name === "UserNotFoundException") {
        setErr("No account found with this email. Please request an account first.");
      } else {
        // Catch-all for any error mentioning disabled/enabled
        if (error.message?.toLowerCase().includes("disabled") ||
            error.message?.toLowerCase().includes("not enabled")) {
          setErr("Your account is still pending admin approval. Please wait for a confirmation email.");
        } else {
          setErr(error.message || "Sign-in failed. Please try again.");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-6">
          <img
            src="infauxtech_logo_rectangle.png"
            alt="Media Forensics Hub"
            className="w-32 mx-auto mb-4"
          />
          <h2 className="text-2xl font-bold text-purple-800">Sign In</h2>
          <p className="text-gray-600 mt-1 text-sm">
            Sign in to your account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-left text-gray-700 font-medium mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-left text-gray-700 font-medium">
                Password
              </label>
              <a
                href="/forgot-password"
                className="text-purple-700 text-sm hover:underline"
              >
                Forgot your password?
              </a>
            </div>


            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none pr-10"
                required
                autoComplete="current-password"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={() => setShowPassword(!showPassword)}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label htmlFor="showPassword" className="ml-2 text-sm text-gray-700">
                  Show
                </label>
              </div>
            </div>

          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-purple-700 text-white font-semibold py-2 rounded-lg hover:bg-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        {err && (
          <p className="mt-4 text-red-600 text-center font-medium text-sm">
            {err}
          </p>
        )}

        <div className="mt-6 text-center space-y-2">
          <p className="text-gray-600 text-sm">
            Don't have an account?{" "}
            <a
              href="/request-account"
              className="text-purple-700 font-semibold hover:underline"
            >
              Request one
            </a>
          </p>

          {/* Optional: Add forgot password link if you implement that flow */}
          {/* <a
            href="/forgot-password"
            className="block text-purple-700 text-sm hover:underline"
          >
            Forgot your password?
          </a> */}
        </div>
      </div>
    </div>
  );
}
